import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Lightbulb, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';
import { solutions } from '../mockData';
import { useLanguage } from '../i18n/LanguageContext';

const Solutions = () => {
  const { t } = useLanguage();
  const steps = t('solutions.steps');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Solutions for Paper Makers"
        description="Comprehensive solutions for paper manufacturers - improve paper strength, reduce production costs, solve water absorption problems, and ensure environmental compliance with VB Global's expert solutions."
        keywords="paper maker solutions, paper production problems, paper strength improvement, paper formation, paper quality, paper mill consulting, paper industry solutions, paper machine optimization, retention drainage problems, sizing problems, eco-friendly paper production, VB Global"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 data-testid="solutions-hero-title" className="text-4xl md:text-5xl font-bold mb-6">
              {t('solutions.heroTitle')}
            </h1>
            <p className="text-xl text-gray-200">
              {t('solutions.heroSub')}
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('solutions.introTitle')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('solutions.introText')}
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-[#8B2C3A] p-2 rounded-lg">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('solutions.expertConsultation')}</h3>
                    <p className="text-gray-600">{t('solutions.expertConsultationDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-600 p-2 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('solutions.provenResults')}</h3>
                    <p className="text-gray-600">{t('solutions.provenResultsDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80" 
                alt="Paper production solutions"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Accordion */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('solutions.faqTitle')}
          </h2>
          
          <Accordion type="single" collapsible className="space-y-4">
            {solutions.map((solution) => (
              <AccordionItem key={solution.id} value={`item-${solution.id}`} className="bg-white border-2 rounded-lg px-6">
                <AccordionTrigger className="text-left hover:text-[#8B2C3A] transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl font-bold text-[#8B2C3A] mt-1">{solution.id < 10 ? `0${solution.id}` : solution.id}</span>
                    <span className="text-lg font-semibold">{solution.problem}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <div className="space-y-4 pl-12">
                    <div>
                      <h4 className="font-semibold text-[#8B2C3A] mb-2">{t('solutions.ourSolution')}</h4>
                      <p className="text-gray-700">{solution.solution}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('solutions.recommendedProducts')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {solution.products.map((product, idx) => (
                          <span key={idx} className="bg-[#8B2C3A]/10 text-[#8B2C3A] px-3 py-1 rounded-full text-sm font-medium">
                            {product}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('solutions.keyBenefits')}</h4>
                      <div className="space-y-2">
                        {solution.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t('solutions.processTitle')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <Card key={i} className="text-center border-2 hover:border-[#8B2C3A] transition-colors">
                <CardHeader>
                  <div className="w-16 h-16 bg-[#8B2C3A] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                    {i + 1}
                  </div>
                  <CardTitle className="text-lg">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{step.desc}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('solutions.ctaTitle')}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {t('solutions.ctaSub')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
              {t('solutions.ctaButton')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
